/**
 * Excel Export — Hebrew / RTL via native sheet direction.
 *
 * Produces two output shapes:
 *
 *  • exportWeeklyExcel()  → one workbook with:
 *      - a summary sheet (participants × days)
 *      - one presentation sheet per operational day (mirrors the on-screen grid)
 *      - a raw-data sheet (one row per slot) with AutoFilter for sort/filter/pivot
 *
 *  • exportDailyExcel()   → one workbook with a single presentation sheet for the chosen day
 *
 * Unlike the PDF exporter, Excel shapes bidirectional text natively — we simply
 * set `worksheet.views[].rightToLeft = true` and write Hebrew strings as-is.
 * No character-reversal helper is needed.
 */

import type { Fill, Worksheet } from 'exceljs';
import { Workbook } from 'exceljs';
import type { AssignmentStatus, Level, Participant, Schedule, SlotRequirement, Task } from '../models/types';
import { escapeFormulaInjection } from '../shared/utils/formula-injection';
import { fmtTime } from '../utils/date-utils';
import { buildDay0Schedule } from './day0-adapter';
import { foldedColumnNames, getDayWindow, getFoldedStartTimes, getTasksForDay, rgbToArgb, tint } from './export-utils';
import { computeSectionMetrics, getTaskAssignments, inferColumnStrategy } from './layout-engine';

// ─── Style constants ─────────────────────────────────────────────────────────

const HEADER_FILL_ARGB = 'FF374151'; // dark slate, matches PDF `headStyles.fillColor`
const HEADER_FONT_ARGB = 'FFFFFFFF';
const THIN_BORDER = {
  top: { style: 'thin' as const, color: { argb: 'FFD2D2D2' } },
  bottom: { style: 'thin' as const, color: { argb: 'FFD2D2D2' } },
  left: { style: 'thin' as const, color: { argb: 'FFD2D2D2' } },
  right: { style: 'thin' as const, color: { argb: 'FFD2D2D2' } },
};

function solidFill(argb: string): Fill {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb } };
}

/** Sanitise a string for use as an Excel sheet name (≤31 chars, no `\/*?:[]`). */
function safeSheetName(raw: string): string {
  const cleaned = raw.replace(/[\\/*?:[\]]/g, '-').trim();
  return (cleaned || 'Sheet').substring(0, 31);
}

/** Format a `Level` enum value as `L0` / `L2` / etc. */
function fmtLevel(l: Level | undefined): string {
  return l == null ? '' : `L${l}`;
}

/** Format a participant's status enum, or empty if no assignment. */
function fmtStatus(s: AssignmentStatus | undefined): string {
  return s ?? '';
}

// ─── Presentation sheet (one per day) ────────────────────────────────────────

/**
 * Render a single operational day onto a worksheet as a vertically stacked
 * set of section tables. Uses the same column strategies as the on-screen
 * layout engine, so output structure matches what the user sees.
 */
function buildDaySheet(ws: Worksheet, schedule: Schedule, dayIndex: number, dayStartHour: number): void {
  ws.views = [{ rightToLeft: true, state: 'frozen', ySplit: 2 }];
  ws.pageSetup.orientation = 'landscape';
  ws.pageSetup.fitToPage = true;
  ws.pageSetup.margins = { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 };

  const { start: dayStart } = getDayWindow(schedule, dayIndex, dayStartHour);
  const dayTasks = getTasksForDay(schedule, dayIndex, dayStartHour);

  // Title row (merged later once we know the widest section).
  // Day 0 is the previous-schedule continuity context — clearly mark the
  // sheet so it can never be confused with a generated day.
  const isDay0 = dayIndex === 0;
  const titleCell = ws.getCell(1, 1);
  titleCell.value = isDay0 ? 'יום 0 — הקשר מהשבצ"ק הקודם · קריאה בלבד' : `יום ${dayIndex}`;
  titleCell.font = { bold: true, size: 14, color: isDay0 ? { argb: 'FF8B6914' } : undefined };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
  if (isDay0) titleCell.fill = solidFill('FFFEF3C7');
  ws.getRow(1).height = isDay0 ? 26 : 22;

  if (dayTasks.length === 0) {
    const empty = ws.getCell(3, 1);
    empty.value = 'אין משימות ביום זה';
    empty.font = { italic: true, color: { argb: 'FF888888' } };
    empty.alignment = { horizontal: 'center' };
    ws.mergeCells(1, 1, 1, 6);
    ws.mergeCells(3, 1, 3, 6);
    ws.getColumn(1).width = 14;
    for (let c = 2; c <= 6; c++) ws.getColumn(c).width = 18;
    return;
  }

  const sections = computeSectionMetrics(dayTasks);
  let currentRow = 3; // leave row 2 blank under the title
  let maxCols = 2;

  for (const section of sections) {
    const strategy = inferColumnStrategy(section.tasks);
    const columns = strategy(section.tasks);
    if (columns.length === 0) continue;

    const uniqueTimes = getFoldedStartTimes(section.tasks);
    const sectionColor = section.color;
    const tintedHeader = rgbToArgb(tint(sectionColor, 0.55));
    const tintedCell = rgbToArgb(tint(sectionColor));

    const totalCols = columns.length + 1; // + time column
    maxCols = Math.max(maxCols, totalCols);

    // Section title row (merged across all columns)
    const sectionTitleCell = ws.getCell(currentRow, 1);
    sectionTitleCell.value = escapeFormulaInjection(section.title);
    sectionTitleCell.font = { bold: true, size: 12 };
    sectionTitleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    sectionTitleCell.fill = solidFill(tintedHeader);
    sectionTitleCell.border = THIN_BORDER;
    ws.mergeCells(currentRow, 1, currentRow, totalCols);
    ws.getRow(currentRow).height = 20;
    currentRow++;

    // Header row: [ זמן | col1 | col2 | ... ]
    const headerRow = ws.getRow(currentRow);
    const headerValues = ['זמן', ...columns.map((c) => c.header)];
    for (let i = 0; i < headerValues.length; i++) {
      const cell = headerRow.getCell(i + 1);
      // Column headers derive from user-controlled task/source/sub-team labels.
      cell.value = escapeFormulaInjection(headerValues[i]);
      cell.font = { bold: true, color: { argb: HEADER_FONT_ARGB } };
      cell.fill = solidFill(HEADER_FILL_ARGB);
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = THIN_BORDER;
    }
    headerRow.height = 18;
    currentRow++;

    // Data rows — one row per unique start time
    for (const timeNum of uniqueTimes) {
      const time = new Date(timeNum);
      const timeTasks = section.tasks.filter((t) => new Date(t.timeBlock.start).getTime() === timeNum);
      const row = ws.getRow(currentRow);

      // Time cell
      const timeCell = row.getCell(1);
      timeCell.value = fmtTime(time);
      timeCell.font = { bold: true };
      timeCell.alignment = { horizontal: 'center', vertical: 'middle' };
      timeCell.border = THIN_BORDER;

      // Data cells, one per column definition
      let maxCardsInRow = 1;
      for (let c = 0; c < columns.length; c++) {
        const col = columns[c];
        // Fold split #a/#b halves into one "A / B" entry — same treatment as
        // the on-screen grid and the PDF (foldedColumnNames mirrors the PDF's
        // columnNameEntries). With no split tasks this is the plain per-slot
        // name collection. Empty slot/half placeholder stays '(פנוי)'.
        const names = foldedColumnNames(timeTasks, col, schedule, '(פנוי)');
        const cell = row.getCell(c + 2);
        // Escape the joined cell value (not each name): a spreadsheet evaluates
        // a cell as a formula only by its leading character, so escaping per-name
        // would turn the guard apostrophe into a literal mid-cell character.
        cell.value = names.length ? escapeFormulaInjection(names.join('\n')) : '—';
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = THIN_BORDER;
        if (names.length) {
          cell.fill = solidFill(tintedCell);
        } else {
          cell.font = { color: { argb: 'FFBEBEBE' } };
        }
        maxCardsInRow = Math.max(maxCardsInRow, names.length || 1);
      }
      row.height = Math.max(22, maxCardsInRow * 15 + 6);
      currentRow++;
    }

    // Blank separator row between sections
    currentRow++;
  }

  // Now merge the title row across the widest section
  if (maxCols > 1) ws.mergeCells(1, 1, 1, maxCols);

  // Column widths
  ws.getColumn(1).width = 14; // time
  for (let c = 2; c <= maxCols; c++) {
    ws.getColumn(c).width = 22;
  }
}

// ─── Summary sheet ───────────────────────────────────────────────────────────

/**
 * Build a participants × days matrix. Each cell lists the tasks that
 * participant is assigned to that day, joined by `, `.
 */
function buildSummarySheet(ws: Worksheet, schedule: Schedule, dayStartHour: number): void {
  ws.views = [{ rightToLeft: true, state: 'frozen', ySplit: 1, xSplit: 1 }];

  // Frozen op-day count — NOT getNumDays (a task-bearing-day cardinality that,
  // used as an absolute 1-based index bound, silently drops gappy/late op-days
  // and breaks lock-step with the on-screen grid). See getNumDays' doc.
  const numDays = schedule.periodDays;

  // Precompute day → tasks so we don't recompute per participant
  const tasksByDay: Task[][] = [];
  const dayDates: Date[] = [];
  for (let d = 1; d <= numDays; d++) {
    tasksByDay.push(getTasksForDay(schedule, d, dayStartHour));
    dayDates.push(getDayWindow(schedule, d, dayStartHour).start);
  }

  // Header row
  const headerValues = ['משתתף', ...dayDates.map((_, i) => `יום ${i + 1}`)];
  const headerRow = ws.addRow(headerValues);
  headerRow.font = { bold: true, color: { argb: HEADER_FONT_ARGB } };
  headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
  headerRow.height = 22;
  for (let i = 1; i <= headerValues.length; i++) {
    headerRow.getCell(i).fill = solidFill(HEADER_FILL_ARGB);
    headerRow.getCell(i).border = THIN_BORDER;
  }

  // Participants sorted by name for a deterministic matrix
  const participants = [...schedule.participants].sort((a, b) => a.name.localeCompare(b.name, 'he'));

  for (const p of participants) {
    const rowValues: (string | number)[] = [escapeFormulaInjection(p.name)];
    for (let d = 0; d < numDays; d++) {
      const tasks = tasksByDay[d];
      const taskNames = new Set<string>();
      for (const t of tasks) {
        const has = schedule.assignments.some((a) => a.taskId === t.id && a.participantId === p.id);
        if (has) taskNames.add(t.sourceName || t.name);
      }
      rowValues.push(escapeFormulaInjection([...taskNames].join(', ')));
    }
    const row = ws.addRow(rowValues);
    row.getCell(1).font = { bold: true };
    row.getCell(1).alignment = { horizontal: 'right', vertical: 'middle' };
    row.getCell(1).border = THIN_BORDER;
    for (let c = 2; c <= numDays + 1; c++) {
      const cell = row.getCell(c);
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = THIN_BORDER;
    }
    row.height = Math.max(20, 16);
  }

  ws.getColumn(1).width = 22;
  for (let c = 2; c <= numDays + 1; c++) ws.getColumn(c).width = 20;
}

// ─── Raw data sheet ──────────────────────────────────────────────────────────

/**
 * Flat assignments table: one row per `SlotRequirement` (filled or unfilled).
 * Times are written as real `Date` values with `hh:mm` number format so
 * sort/filter/pivot work correctly. Includes AutoFilter.
 *
 * Note: calendar dates and weekdays are intentionally omitted — schedules are
 * day-index based, so we expose `יום` (index 1..N) only.
 */
function buildRawDataSheet(ws: Worksheet, schedule: Schedule, dayStartHour: number): void {
  ws.views = [{ rightToLeft: true, state: 'frozen', ySplit: 1 }];

  const headers = [
    'יום',
    'התחלה',
    'סיום',
    'קטגוריה',
    'משימה',
    'תת-צוות',
    'תווית משבצת',
    'רמות מותרות',
    'הסמכות נדרשות',
    'משתתף',
    'קבוצה',
    'רמה',
    'הסמכות משתתף',
    'סטטוס',
    'taskId',
    'slotId',
    'participantId',
  ];
  ws.addRow(headers);

  // Resolve cert IDs to display labels via the schedule's frozen snapshot —
  // never reach into the live store from a schedule-screen code path.
  const certLabel = (id: string): string => schedule.certLabelSnapshot?.[id] ?? id;

  // Frozen op-day count — NOT getNumDays (a task-bearing-day cardinality that,
  // used as an absolute 1-based index bound, silently drops gappy/late op-days
  // and breaks lock-step with the on-screen grid). See getNumDays' doc.
  const numDays = schedule.periodDays;
  for (let d = 1; d <= numDays; d++) {
    const dayTasks = getTasksForDay(schedule, d, dayStartHour);
    for (const task of dayTasks) {
      const assignedSlots = getTaskAssignments(task, schedule);
      for (const as of assignedSlots) {
        const slot: SlotRequirement = as.slot;
        const p: Participant | undefined = as.participant;
        const startDt = new Date(task.timeBlock.start);
        const endDt = new Date(task.timeBlock.end);
        // Escape every user-controlled free-text column against CSV/formula
        // injection. Numeric (d), Date (start/end), enum-derived (levels,
        // status) and system-id columns can't carry a leading formula trigger
        // and are left as-is.
        ws.addRow([
          d,
          startDt,
          endDt,
          escapeFormulaInjection(task.sectionKey || (task.sourceName || task.name).toLowerCase()),
          escapeFormulaInjection(task.sourceName || task.name),
          escapeFormulaInjection(slot.subTeamLabel || slot.subTeamId || ''),
          escapeFormulaInjection(slot.label || ''),
          slot.acceptableLevels.map((l) => fmtLevel(l.level)).join('/'),
          escapeFormulaInjection(slot.requiredCertifications.map(certLabel).join(', ')),
          escapeFormulaInjection(p?.name || ''),
          escapeFormulaInjection(p?.group || ''),
          fmtLevel(p?.level),
          escapeFormulaInjection(p ? p.certifications.map(certLabel).join(', ') : ''),
          fmtStatus(as.assignment?.status),
          task.id,
          slot.slotId,
          p?.id || '',
        ]);
      }
    }
  }

  // Header styling
  const headerRow = ws.getRow(1);
  headerRow.font = { bold: true, color: { argb: HEADER_FONT_ARGB } };
  headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
  for (let i = 1; i <= headers.length; i++) {
    headerRow.getCell(i).fill = solidFill(HEADER_FILL_ARGB);
  }

  // Column widths (roughly tuned for content)
  const widths = [5, 12, 9, 10, 14, 18, 14, 18, 14, 20, 18, 14, 7, 22, 12, 16, 16, 16];
  for (let i = 0; i < headers.length; i++) {
    ws.getColumn(i + 1).width = widths[i] ?? 14;
  }

  // Number formats for time columns (התחלה, סיום)
  ws.getColumn(2).numFmt = 'hh:mm';
  ws.getColumn(3).numFmt = 'hh:mm';

  // Alignment for data rows
  const lastRow = ws.rowCount;
  for (let r = 2; r <= lastRow; r++) {
    const row = ws.getRow(r);
    for (let c = 1; c <= headers.length; c++) {
      row.getCell(c).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    }
  }

  // AutoFilter on all columns
  ws.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: headers.length },
  };
}

// ─── Download helper ─────────────────────────────────────────────────────────

async function triggerDownload(workbook: Workbook, filename: string): Promise<void> {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer as ArrayBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Public entry points ─────────────────────────────────────────────────────

/**
 * Build and download a full-week workbook:
 *   - summary sheet (participants × days)
 *   - one presentation sheet per operational day
 *   - raw-data sheet with AutoFilter
 */
export async function exportWeeklyExcel(
  schedule: Schedule,
  dayStartHour: number = 5,
  includeDay0: boolean = true,
): Promise<void> {
  const workbook = new Workbook();
  workbook.creator = 'Garden Manager';
  workbook.created = new Date();

  // 1. Summary sheet (first tab) — always excludes Day 0 so totals reflect
  //    THIS schedule.
  const summaryWs = workbook.addWorksheet(safeSheetName('סיכום'));
  buildSummarySheet(summaryWs, schedule, dayStartHour);

  // 2a. Day 0 presentation sheet (continuity context) — only when continuity
  //     is attached and the user kept the export-dialog checkbox on.
  const day0 = includeDay0 ? buildDay0Schedule(schedule) : null;
  if (day0) {
    const ws = workbook.addWorksheet(safeSheetName('יום 0 - הקשר'));
    buildDaySheet(ws, day0, 0, dayStartHour);
  }

  // 2b. One presentation sheet per real day.
  // Frozen op-day count — NOT getNumDays (a task-bearing-day cardinality that,
  // used as an absolute 1-based index bound, silently drops gappy/late op-days
  // and breaks lock-step with the on-screen grid). See getNumDays' doc.
  const numDays = schedule.periodDays;
  for (let d = 1; d <= numDays; d++) {
    const sheetName = safeSheetName(`יום ${d}`);
    const ws = workbook.addWorksheet(sheetName);
    buildDaySheet(ws, schedule, d, dayStartHour);
  }

  // 3. Raw data sheet (last tab)
  const rawWs = workbook.addWorksheet(safeSheetName('נתונים גולמיים'));
  buildRawDataSheet(rawWs, schedule, dayStartHour);

  await triggerDownload(workbook, 'GardenManager-Schedule.xlsx');
}

/**
 * Build and download a single-day workbook with one presentation sheet.
 *
 * dayIndex=0 produces a Day 0 (continuity context) sheet. Falls back to
 * day 1 if continuity isn't attached.
 */
export async function exportDailyExcel(schedule: Schedule, dayIndex: number, dayStartHour: number = 5): Promise<void> {
  const workbook = new Workbook();
  workbook.creator = 'Garden Manager';
  workbook.created = new Date();

  if (dayIndex === 0) {
    const day0 = buildDay0Schedule(schedule);
    if (day0) {
      const ws = workbook.addWorksheet(safeSheetName('יום 0 - הקשר'));
      buildDaySheet(ws, day0, 0, dayStartHour);
      await triggerDownload(workbook, `GardenManager-Day0-Context.xlsx`);
      return;
    }
    dayIndex = 1;
  }

  const sheetName = safeSheetName(`יום ${dayIndex}`);
  const ws = workbook.addWorksheet(sheetName);
  buildDaySheet(ws, schedule, dayIndex, dayStartHour);

  await triggerDownload(workbook, `GardenManager-Day-${dayIndex}.xlsx`);
}
